import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  createThread,
  deleteThread,
  favoriteThread,
  loadThread,
  selectChart
} from '../../../../actions/aiAnalyticsActions';
import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import { MANAGE_AI_SEMANTIC_MODEL } from '../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../services/auth';
import { buildOpLabelMaps, sortThreads } from '../AiAnalyticsChat.service';
import { AI_COLORS, SEMANTIC_MODEL_PATH } from '../aiAnalyticsConstants';

import SavedChartsList from './SavedChartsList';
import ThreadListItem from './ThreadListItem';

export default function ThreadSidebar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManageSemantic = userHasAccessTo(MANAGE_AI_SEMANTIC_MODEL);
  const { threads, activeThread, pendingAsks, pendingOps, savedCharts, selectedChart } = useSelector(
    state => state.aiAnalytics
  );
  const [search, setSearch] = useState('');

  const term = search.trim().toLowerCase();
  const visibleThreads = sortThreads(threads.filter(t => !term || (t.title || '').toLowerCase().includes(term)));
  const visibleCharts = savedCharts.filter(c => !term || (c.title || '').toLowerCase().includes(term));
  const { threadOpLabels, chartOpLabels } = buildOpLabelMaps(pendingOps);

  function handleSelectThread(threadId) {
    dispatch(loadThread(threadId));
  }

  function handleDeleteThread(e, threadId) {
    e.stopPropagation();
    ModalBoxes.confirm({
      content: 'Are you sure you want to delete this thread?',
      confirmButton: 'Yes',
      cancelButton: 'No'
    }).then(
      () => {
        dispatch(deleteThread(threadId));
      },
      () => {}
    );
  }

  function handleToggleFavorite(e, thread) {
    e.stopPropagation();
    dispatch(favoriteThread(thread.id, !thread.favorite));
  }

  return (
    <div className="thread-sidebar">
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => dispatch(createThread())}
        fullWidth
        sx={{ mb: 1.5, backgroundColor: AI_COLORS.primary, '&:hover': { backgroundColor: AI_COLORS.primaryDark } }}
      >
        New Analysis
      </Button>

      {canManageSemantic && (
        <Button
          variant="text"
          size="small"
          startIcon={<SchemaOutlinedIcon />}
          onClick={() => navigate(SEMANTIC_MODEL_PATH)}
          fullWidth
          sx={{ mb: 1.5, color: AI_COLORS.brand, justifyContent: 'flex-start' }}
        >
          Semantic Model
        </Button>
      )}

      <TextField
        size="small"
        fullWidth
        placeholder="Search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />

      <div className="sidebar-sections">
        <div className="sidebar-section">
          <Typography variant="overline" color="text.secondary">
            Threads
          </Typography>
          <div className="sidebar-section-list">
            {visibleThreads.map(function(thread) {
              return (
                <ThreadListItem
                  key={thread.id}
                  thread={thread}
                  active={activeThread?.id === thread.id && !selectedChart}
                  askPhase={pendingAsks[thread.id]?.phase ?? thread.activeAsk?.phase}
                  opLabel={threadOpLabels[thread.id]}
                  onSelect={handleSelectThread}
                  onToggleFavorite={handleToggleFavorite}
                  onDelete={handleDeleteThread}
                />
              );
            })}
            {visibleThreads.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                No threads.
              </Typography>
            )}
          </div>
        </div>

        <div className="sidebar-section">
          <Typography variant="overline" color="text.secondary">
            Saved Charts
          </Typography>
          <div className="sidebar-section-list">
            <SavedChartsList
              charts={visibleCharts}
              selectedChartId={selectedChart?.id}
              opLabels={chartOpLabels}
              onSelect={chart => dispatch(selectChart(chart))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
